import { useState } from "react";
import AdminCard from "./AdminCard";

interface Props { onSave: () => void; }

const defaultProperties = [
  { id: 1, title: "Birchfield House", location: "Leeds, West Yorkshire", type: "Assisted Living", price: "£1.2M", beds: 14, sqft: 4200, tag: "Flagship Asset", status: "Active" },
  { id: 2, title: "Meadowbrook Court", location: "Sheffield, South Yorkshire", type: "Supported Housing", price: "£875K", beds: 10, sqft: 3100, tag: "Fully Leased", status: "Active" },
  { id: 3, title: "Ashford Lodge", location: "Manchester, Greater Manchester", type: "Assisted Living", price: "£1.05M", beds: 12, sqft: 3800, tag: "New Acquisition", status: "Active" },
  { id: 4, title: "Riverside View", location: "York, North Yorkshire", type: "Supported Housing", price: "£720K", beds: 8, sqft: 2600, tag: "Fully Leased", status: "Active" },
];

export default function AdminProperties({ onSave }: Props) {
  const [properties, setProperties] = useState(defaultProperties);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [headerTitle, setHeaderTitle] = useState("Featured Property Assets");
  const [headerSub, setHeaderSub] = useState("UK assisted living and supported housing properties forming the asset base of the Thorpe Dellow Social Housing Structured Product.");
  const [portfolioStats, setPortfolioStats] = useState([
    { value: "35", label: "Properties" },
    { value: "£47M+", label: "Combined AUM" },
    { value: "545+", label: "Residential Units" },
    { value: "3 Nations", label: "ENG · WAL · SCO" },
  ]);

  const updateProp = (id: number, k: string, v: string | number) => {
    setProperties((prev) => prev.map((p) => p.id === id ? { ...p, [k]: v } : p));
  };
  const updatePortfolioStat = (i: number, k: string, v: string) => {
    setPortfolioStats((prev) => prev.map((s, idx) => idx === i ? { ...s, [k]: v } : s));
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#071A2E]" style={{ fontFamily: "'Playfair Display', serif" }}>Properties</h1>
        <p className="text-slate-500 text-sm mt-1">Manage the featured property assets shown on the homepage and properties page.</p>
      </div>

      <AdminCard title="Section Header">
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Section Title</label>
            <input value={headerTitle} onChange={(e) => setHeaderTitle(e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Section Subtitle</label>
            <textarea value={headerSub} onChange={(e) => setHeaderSub(e.target.value)} rows={2} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C] resize-none" />
          </div>
        </div>
      </AdminCard>

      <AdminCard title="Portfolio Stats (Properties Page Header)">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {portfolioStats.map((s, i) => (
            <div key={i} className="bg-slate-50 rounded-xl p-3 border border-slate-100">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Value</label>
                  <input value={s.value} onChange={(e) => updatePortfolioStat(i, "value", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C9A84C]" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Label</label>
                  <input value={s.label} onChange={(e) => updatePortfolioStat(i, "label", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C9A84C]" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </AdminCard>

      <AdminCard
        title="Property Listings"
        actions={
          <button
            onClick={() => {
              const newId = Math.max(...properties.map((p) => p.id)) + 1;
              setProperties((prev) => [...prev, { id: newId, title: "New Property", location: "Location, UK", type: "Assisted Living", price: "£0", beds: 0, sqft: 0, tag: "New", status: "Active" }]);
              setExpandedId(newId);
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#C9A84C]/10 text-[#C9A84C] text-xs font-semibold hover:bg-[#C9A84C]/20 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line leading-none" />
            Add Property
          </button>
        }
      >
        <div className="space-y-3">
          {properties.map((p) => (
            <div key={p.id} className="border border-slate-200 rounded-xl overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 bg-slate-50">
                <button
                  onClick={() => setExpandedId(expandedId === p.id ? null : p.id)}
                  className="flex items-center gap-3 flex-1 text-left cursor-pointer"
                >
                  <span className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0" />
                  <span className="text-sm font-semibold text-[#071A2E]">{p.title}</span>
                  <span className="text-xs text-slate-400 hidden sm:inline">· {p.location}</span>
                </button>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setProperties((prev) => prev.filter((x) => x.id !== p.id))}
                    className="w-7 h-7 flex items-center justify-center rounded-lg text-red-400 hover:bg-red-50 transition-colors cursor-pointer"
                  >
                    <i className="ri-delete-bin-line text-sm leading-none" />
                  </button>
                  <i className={expandedId === p.id ? "ri-arrow-up-s-line text-slate-400 leading-none cursor-pointer" : "ri-arrow-down-s-line text-slate-400 leading-none cursor-pointer"} onClick={() => setExpandedId(expandedId === p.id ? null : p.id)} />
                </div>
              </div>

              {expandedId === p.id && (
                <div className="px-4 py-4 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5">Property Name</label>
                      <input value={p.title} onChange={(e) => updateProp(p.id, "title", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5">Location</label>
                      <input value={p.location} onChange={(e) => updateProp(p.id, "location", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5">Type</label>
                      <select value={p.type} onChange={(e) => updateProp(p.id, "type", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C] cursor-pointer">
                        <option>Assisted Living</option>
                        <option>Supported Housing</option>
                        <option>Care Home</option>
                        <option>Residential</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5">Price / Value</label>
                      <input value={p.price} onChange={(e) => updateProp(p.id, "price", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5">Units / Beds</label>
                      <input type="number" value={p.beds} onChange={(e) => updateProp(p.id, "beds", parseInt(e.target.value) || 0)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5">Sqft</label>
                      <input type="number" value={p.sqft} onChange={(e) => updateProp(p.id, "sqft", parseInt(e.target.value) || 0)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5">Tag / Badge</label>
                      <input value={p.tag} onChange={(e) => updateProp(p.id, "tag", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C]" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5">Status</label>
                      <select value={p.status} onChange={(e) => updateProp(p.id, "status", e.target.value)} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[#C9A84C] cursor-pointer">
                        <option>Active</option>
                        <option>Pending</option>
                        <option>Sold</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </AdminCard>

      <div className="flex justify-end">
        <button onClick={onSave} className="flex items-center gap-2 px-6 py-3 rounded-xl bg-[#071A2E] text-white text-sm font-semibold hover:bg-[#0D2B4A] transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-save-line leading-none" />
          Save Changes
        </button>
      </div>
    </div>
  );
}
